import React, { useCallback, useRef, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpFromLine } from '@fortawesome/pro-light-svg-icons/faArrowUpFromLine';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { catalogSongMessages } from './catalogSongMessages';
import { MASTER_ACCEPT } from './masterFile';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the song files; each part uses its own slice of it */
import styles from './SongFiles.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface MasterDropZoneProps {
  title: string;
  hint: string;
  disabled?: boolean;
  onFile: (file: File) => void;
}

/** The empty master field (designs f3, k1, k3): drop a file here, or pick one. */
const MasterDropZoneComponent: React.FC<MasterDropZoneProps> = ({ title, hint, disabled = false, onFile }) => {
  const intl = useIntl();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isOver, setIsOver] = useState(false);

  const pick = useCallback(() => inputRef.current?.click(), []);

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (file !== undefined) {
        onFile(file);
      }
    },
    [onFile]
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsOver(true);
  }, []);

  const onDragLeave = useCallback(() => setIsOver(false), []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setIsOver(false);
      const file = event.dataTransfer.files[0];
      if (file !== undefined && !disabled) {
        onFile(file);
      }
    },
    [disabled, onFile]
  );

  return (
    <div
      className={clsx(styles.drop, isOver && styles.dropOver)}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <input
        ref={inputRef}
        type="file"
        accept={MASTER_ACCEPT}
        hidden
        onChange={onChange}
        data-testid="master-file-input"
      />
      <span className={styles.dropIcon}>
        <FontAwesomeIcon icon={faArrowUpFromLine} />
      </span>
      <div className={styles.grow}>
        <span className={styles.strong}>{title}</span>
        <span className={styles.small}>{hint}</span>
      </div>
      <SMLButton size="sm" outline omitCap disabled={disabled} onClick={pick}>
        {intl.formatMessage(catalogSongMessages.pick)}
      </SMLButton>
    </div>
  );
};

export const MasterDropZone = TypedMemo(MasterDropZoneComponent);
